/*
 * Client-side submission to the intake API.
 *
 * The application is submitted ONCE, at the end of the flow, to our own
 * backend route (which validates, maps to the lender's schema and forwards using
 * the server-held OAuth credentials — no secrets in this bundle):
 *
 *   POST {submitUrl}   default "/api/v1/applications"
 *   body: { applicationId, product, form, attribution }
 *
 * Embedded on a different origin than the API? Pass the absolute URL:
 *   ApplicationForm.mount('#el', { submitUrl: 'https://your-app.vercel.app/api/v1/applications' })
 */

import { reportError } from "../lib/sentryClient.js";
import { BRAND } from "../brand.js";

const simRef = () => `${BRAND.referencePrefix || "APP"}-` + (Date.now() % 1000000).toString().padStart(6, "0");

/* Report an intake-call failure (never form data — just where/status/message).
   422 validation is the applicant's normal path, not an incident. */
function reportApiFailure(where, url, err) {
  if (err && err.status === 422) return;
  reportError(err, { where, url, status: err && err.status });
}

export function createIntakeClient(config = {}) {
  const cfg = {
    submitUrl: "/api/v1/applications",
    initiateUrl: null,       // bank-session endpoint; derived from submitUrl when unset
    draftsUrl: null,         // drafts endpoint; derived from submitUrl when unset
    headers: {},
    transport: null,         // optional (body, cfg) => Promise<any> - full override
    simulateFallback: false, // resolve with a sim reference if the API is unreachable (dev)
    debug: false,
    ...config,
  };
  if (!cfg.initiateUrl) {
    cfg.initiateUrl = /\/applications$/.test(cfg.submitUrl)
      ? cfg.submitUrl.replace(/\/applications$/, "/bank-session")
      : "/api/v1/bank-session";
  }
  if (!cfg.draftsUrl) {
    cfg.draftsUrl = /\/applications$/.test(cfg.submitUrl)
      ? cfg.submitUrl.replace(/\/applications$/, "/drafts")
      : "/api/v1/drafts";
  }
  if (!cfg.abnLookupUrl) {
    cfg.abnLookupUrl = /\/applications$/.test(cfg.submitUrl)
      ? cfg.submitUrl.replace(/\/applications$/, "/abn-lookup")
      : "/api/v1/abn-lookup";
  }

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...cfg.headers },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let data; try { data = JSON.parse(text); } catch { data = { raw: text }; }
    if (!res.ok) {
      const e = new Error(
        data?.error === "validation_failed"
          ? "Some details couldn't be validated - please review your answers and try again."
          : (data && (data.message || data.error)) || `Submission failed (${res.status})`
      );
      e.status = res.status; e.data = data;
      throw e;
    }
    return data;
  }

  return {
    config: cfg,

    /* Per-step server-side draft save (progressive capture / lead registration).
       First call returns { resumeToken } — pass it on every later call.
       Never throws: draft sync must not disturb the applicant. */
    async saveDraft({ applicationId, resumeToken, product, channel, step, form, attribution }) {
      const body = { applicationId, product, step, form, ...(channel ? { channel } : {}), ...(resumeToken ? { resumeToken } : {}), ...(attribution ? { attribution } : {}) };
      try {
        return await postJson(cfg.draftsUrl, body);
      } catch (err) {
        if (cfg.debug) console.warn("[drafts] save failed (non-fatal):", err.message);
        reportApiFailure("draft-save", cfg.draftsUrl, err); // silent for the applicant, loud for us
        return { simulated: true, failed: true };
      }
    },

    /* Resume a server-side draft (magic links / cross-device). Returns the
       draft body or null. */
    async fetchDraft({ applicationId, resumeToken }) {
      try {
        const url = `${cfg.draftsUrl}?id=${encodeURIComponent(applicationId)}&token=${encodeURIComponent(resumeToken)}`;
        const res = await fetch(url, { headers: cfg.headers });
        if (!res.ok) return null;
        return await res.json();
      } catch (err) {
        if (cfg.debug) console.warn("[drafts] fetch failed:", err.message);
        return null;
      }
    },

    /* Step 6: create the lender application and get the bank-statements iframe URL.
       Resolves { applicationId, lenderReference, bankStatementsUrl, simulated }. */
    async initiate({ applicationId, product, channel, form, attribution }) {
      const body = { applicationId, product, form, ...(channel ? { channel } : {}), ...(attribution ? { attribution } : {}) };
      try {
        return await postJson(cfg.initiateUrl, body);
      } catch (err) {
        reportApiFailure("bank-session", cfg.initiateUrl, err);
        if (cfg.simulateFallback) {
          if (cfg.debug) console.warn("[intake] bank-session failed; simulating (simulateFallback on):", err.message);
          return { applicationId, lenderReference: null, bankStatementsUrl: null, simulated: true };
        }
        throw err;
      }
    },

    /* Step-6 companion: has the statements data landed server-side yet?
       (illion delivers it by webhook the moment the applicant finishes.)
       Never throws — polling must stay silent. */
    async bankSessionStatus({ applicationId }) {
      try {
        const res = await fetch(`${cfg.initiateUrl}?id=${encodeURIComponent(applicationId)}`, { headers: cfg.headers });
        if (!res.ok) return { completed: false };
        return await res.json();
      } catch (err) {
        return { completed: false };
      }
    },

    /* Step-6 telemetry: illion's iframe reports progress to the PARENT page
       as JavaScript postMessages (bank_selected, submission_complete, ...) -
       there is no server-side webhook for those events, only for the
       statements data itself. The panel relays each one here so ops can see
       the applicant's bank-connect progress in the console's Webhooks
       screen. Fire-and-forget - never throws, never blocks the applicant. */
    /* Asset finance: hand over illion statements the broker already holds.
       Two paths, chosen by the SERVER: presign + PUT straight to object
       storage when it's configured (big PDFs never touch our functions), or
       base64 through the API when it isn't. Same recipe as the applicant
       upload link. */
    async supplyStatements({ applicationId, file }) {
      if (!applicationId || !file) return { ok: false, error: "missing_file" };
      try {
        const init = await postJson(cfg.initiateUrl, {
          applicationId,
          statements: { action: "init", name: file.name, contentType: file.type, sizeBytes: file.size },
        });
        if (init && init.uploadUrl) {
          const put = await fetch(init.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
          if (!put.ok) return { ok: false, error: "put_failed", status: put.status };
          await postJson(cfg.initiateUrl, { applicationId, statements: { action: "complete", documentId: init.id } });
          return { ok: true };
        }
        // No object storage on this deployment: the bytes ride in the JSON.
        const dataBase64 = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onerror = () => reject(new Error("read_failed"));
          fr.onload = () => resolve(String(fr.result).split(",")[1] || "");
          fr.readAsDataURL(file);
        });
        await postJson(cfg.initiateUrl, {
          applicationId,
          statements: { action: "upload", name: file.name, contentType: file.type, dataBase64 },
        });
        return { ok: true };
      } catch (err) {
        return { ok: false, error: (err && err.data && err.data.error) || "upload_failed", message: err && err.message };
      }
    },

    /* Asset finance: look an ABN up on the Australian Business Register.
       Never throws - the entity step has to keep working when the register
       is down or unconfigured, so a failure comes back as a shaped answer
       the panel can explain. */
    async lookupAbn(abn) {
      try {
        const res = await fetch(`${cfg.abnLookupUrl}?abn=${encodeURIComponent(String(abn || "").replace(/\D/g, ""))}`, { headers: cfg.headers });
        const data = await res.json().catch(() => null);
        if (res.ok && data && !data.error) return { ok: true, record: data };
        return {
          ok: false,
          error: (data && data.error) || "lookup_failed",
          message: (data && data.message) || "We couldn't check that ABN just now.",
          available: !(data && data.error === "not_configured"),
        };
      } catch {
        return { ok: false, error: "lookup_failed", message: "We couldn't reach the business register.", available: true };
      }
    },

    async relayBankEvent({ applicationId, event }) {
      if (!applicationId || !event) return { ok: false };
      try {
        return await postJson(cfg.initiateUrl, { applicationId, event });
      } catch (err) {
        return { ok: false };
      }
    },

    /* `channel` is display/context only on the wire — the server derives the
       stored channel from the endpoint + verified identity, never from this. */
    async submit({ applicationId, product, channel, form, attribution }) {
      const body = { applicationId, product, form, ...(channel ? { channel } : {}), ...(attribution ? { attribution } : {}) };
      try {
        const data = cfg.transport ? await cfg.transport(body, cfg) : await postJson(cfg.submitUrl, body);
        return {
          reference: (data && data.reference) || null,
          simulated: !!(data && data.simulated),
          raw: data,
        };
      } catch (err) {
        reportApiFailure("submit", cfg.submitUrl, err);
        if (cfg.simulateFallback) {
          if (cfg.debug) console.warn("[intake] submit failed; simulating success (simulateFallback on):", err.message);
          return { reference: simRef(), simulated: true };
        }
        throw err;
      }
    },
  };
}

/* Application ids are v4 UUIDs (the server requires the UUID pattern — it
   keys illion correlation and the lender's id schema, should that
   field be enabled). crypto.randomUUID needs a secure context; fall back to
   getRandomValues for plain-http embeds. */
export function newApplicationId() {
  const c = typeof crypto !== "undefined" ? crypto : null;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && c.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
  const h = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
